import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, TextField, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, Tabs, Tab, Switch, FormControlLabel, Chip
} from '@mui/material';
import { Add, Edit, Delete, Save, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const MASTER_TYPES = [
    { value: 'religion', label: 'Religion' },
    { value: 'caste', label: 'Caste' },
    { value: 'category', label: 'Category' },
    { value: 'concession', label: 'Concession Type' },
    { value: 'minority', label: 'Minority' },
    { value: 'standard', label: 'Standard/Class' },
    { value: 'medium', label: 'Medium of Instruction' },
    { value: 'board', label: 'Board' },
    { value: 'blood_group', label: 'Blood Group' }
];

const SettingsManagement = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [masterData, setMasterData] = useState({});
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ name: '', code: '', description: '', isActive: true });

    useEffect(() => {
        fetchMasterData();
    }, [activeTab]);

    const currentType = MASTER_TYPES[activeTab]?.value || 'religion';

    const fetchMasterData = async () => {
        setLoading(true);
        try {
            const res = await api.get('/master-data', { params: { type: currentType } });
            setMasterData(prev => ({ ...prev, [currentType]: res.data.data || [] }));
        } catch (error) {
            console.error('Failed to fetch master data');
            // Use default data
            setMasterData(prev => ({
                ...prev,
                [currentType]: getDefaultData(currentType)
            }));
        } finally {
            setLoading(false);
        }
    };

    const getDefaultData = (type) => {
        const defaults = {
            religion: [
                { id: 1, name: 'Hindu', code: 'HIN', isActive: true },
                { id: 2, name: 'Muslim', code: 'MUS', isActive: true },
                { id: 3, name: 'Christian', code: 'CHR', isActive: true },
                { id: 4, name: 'Sikh', code: 'SIK', isActive: true },
                { id: 5, name: 'Buddhist', code: 'BUD', isActive: true },
                { id: 6, name: 'Jain', code: 'JAI', isActive: true }
            ],
            caste: [
                { id: 1, name: 'Maratha', code: 'MAR', isActive: true },
                { id: 2, name: 'Brahmin', code: 'BRA', isActive: true },
                { id: 3, name: 'Mali', code: 'MAL', isActive: true },
                { id: 4, name: 'Kunbi', code: 'KUN', isActive: true }
            ],
            category: [
                { id: 1, name: 'General', code: 'GEN', isActive: true },
                { id: 2, name: 'OBC', code: 'OBC', isActive: true },
                { id: 3, name: 'SC', code: 'SC', isActive: true },
                { id: 4, name: 'ST', code: 'ST', isActive: true },
                { id: 5, name: 'NT', code: 'NT', isActive: true },
                { id: 6, name: 'VJ/DT', code: 'VJ', isActive: true },
                { id: 7, name: 'SBC', code: 'SBC', isActive: true }
            ],
            concession: [
                { id: 1, name: 'Full Concession', code: 'FULL', isActive: true },
                { id: 2, name: 'Half Concession', code: 'HALF', isActive: true },
                { id: 3, name: 'EWS Concession', code: 'EWS', isActive: true },
                { id: 4, name: 'Sports Quota', code: 'SPT', isActive: true }
            ],
            minority: [
                { id: 1, name: 'Linguistic Minority', code: 'LM', isActive: true },
                { id: 2, name: 'Religious Minority', code: 'RM', isActive: true }
            ],
            blood_group: [
                { id: 1, name: 'A+', code: 'A+', isActive: true },
                { id: 2, name: 'A-', code: 'A-', isActive: true },
                { id: 3, name: 'B+', code: 'B+', isActive: true },
                { id: 4, name: 'B-', code: 'B-', isActive: true },
                { id: 5, name: 'AB+', code: 'AB+', isActive: true },
                { id: 6, name: 'AB-', code: 'AB-', isActive: true },
                { id: 7, name: 'O+', code: 'O+', isActive: true },
                { id: 8, name: 'O-', code: 'O-', isActive: true }
            ],
            medium: [
                { id: 1, name: 'English', code: 'ENG', isActive: true },
                { id: 2, name: 'Marathi', code: 'MAR', isActive: true },
                { id: 3, name: 'Hindi', code: 'HIN', isActive: true },
                { id: 4, name: 'Semi-English', code: 'SEM', isActive: true }
            ],
            board: [
                { id: 1, name: 'SSC', code: 'SSC', isActive: true },
                { id: 2, name: 'CBSE', code: 'CBSE', isActive: true },
                { id: 3, name: 'ICSE', code: 'ICSE', isActive: true },
                { id: 4, name: 'IB', code: 'IB', isActive: true }
            ],
            standard: [
                { id: 1, name: 'Nursery', code: 'NUR', isActive: true },
                { id: 2, name: 'LKG', code: 'LKG', isActive: true },
                { id: 3, name: 'UKG', code: 'UKG', isActive: true },
                { id: 4, name: '1st', code: '1', isActive: true },
                { id: 5, name: '2nd', code: '2', isActive: true },
                { id: 6, name: '3rd', code: '3', isActive: true },
                { id: 7, name: '4th', code: '4', isActive: true },
                { id: 8, name: '5th', code: '5', isActive: true },
                { id: 9, name: '6th', code: '6', isActive: true },
                { id: 10, name: '7th', code: '7', isActive: true },
                { id: 11, name: '8th', code: '8', isActive: true },
                { id: 12, name: '9th', code: '9', isActive: true },
                { id: 13, name: '10th', code: '10', isActive: true }
            ]
        };
        return defaults[type] || [];
    };

    const handleAdd = () => {
        setEditItem(null);
        setFormData({ name: '', code: '', description: '', isActive: true });
        setOpenDialog(true);
    };

    const handleEdit = (item) => {
        setEditItem(item);
        setFormData({
            name: item.name,
            code: item.code,
            description: item.description || '',
            isActive: item.isActive !== false
        });
        setOpenDialog(true);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Name is required');
            return;
        }
        try {
            const data = { ...formData, type: currentType };
            if (editItem) {
                await api.put(`/master-data/${editItem.id}`, data);
                toast.success('Updated successfully');
            } else {
                await api.post('/master-data', data);
                toast.success('Added successfully');
            }
            setOpenDialog(false);
            fetchMasterData();
        } catch (error) {
            toast.error('Failed to save');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;
        try {
            await api.delete(`/master-data/${id}`);
            toast.success('Deleted successfully');
            fetchMasterData();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const handleToggleActive = async (item) => {
        try {
            await api.put(`/master-data/${item.id}`, { isActive: !item.isActive });
            fetchMasterData();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const currentData = masterData[currentType] || [];

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Settings Management</Typography>

            <Paper sx={{ mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {MASTER_TYPES.map((type, idx) => (
                        <Tab key={type.value} label={type.label} />
                    ))}
                </Tabs>
            </Paper>

            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        {MASTER_TYPES[activeTab]?.label} List ({currentData.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchMasterData}>
                            Refresh
                        </Button>
                        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
                            Add New
                        </Button>
                    </Box>
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Code</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentData.map((item, idx) => (
                                <TableRow key={item.id}>
                                    <TableCell>{idx + 1}</TableCell>
                                    <TableCell>{item.name}</TableCell>
                                    <TableCell><Chip label={item.code} size="small" /></TableCell>
                                    <TableCell>{item.description || '-'}</TableCell>
                                    <TableCell>
                                        <Switch
                                            size="small"
                                            checked={item.isActive !== false}
                                            onChange={() => handleToggleActive(item)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => handleEdit(item)}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {currentData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No data available. Click "Add New" to create entries.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editItem ? 'Edit' : 'Add'} {MASTER_TYPES[activeTab]?.label}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth label="Name" margin="normal" required
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                    />
                    <TextField
                        fullWidth label="Code" margin="normal"
                        value={formData.code}
                        onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    />
                    <TextField
                        fullWidth label="Description" margin="normal" multiline rows={2}
                        value={formData.description}
                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                            />
                        }
                        label="Active"
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" startIcon={<Save />} onClick={handleSave}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SettingsManagement;
